/* eslint-disable */
import {
  Box,
  Button,
  FilledInput,
  FormControl,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  QueryBuilder as QueryBuilderIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { MessageData, useChat } from "context/ChatContext";
import { useAuth } from "context/AuthContext";
import { addMinutes, format } from "date-fns";
import { formatFullAddress, formatPhoneByText } from "utils/formatByText";
import { useAppDispatch } from "store/hooks";
import { useOrders } from "pages/ManageOrders/useOrders";
import * as Styled from "./styles";

const Chat = () => {
  const [currentChat, setCurrentChat] = useState<any>(null);
  const [inputMessage, setInputMessage] = useState("");

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { typeOrders } = useOrders();
  const navigate = useNavigate();
  const { chatData, handleSendNewMessage } = useChat();
  const { dealerId } = useAuth();

  const getChatMessages = (orderId: string) =>
    chatData.filter((chat) => chat.orderId === orderId);

  const hasNewMessage = (messages: MessageData[]) => {
    return messages?.length
      ? messages[messages.length - 1]?.fromId !== dealerId
      : false;
  };

  const opennedChats = typeOrders("InProgress").map((order: any) => {
    const timeCreateAt = addMinutes(
      new Date(order.createdAt),
      order.delivery.eta?.current,
    );
    const deliveryTime = order.delivery.eta?.current
      ? format(timeCreateAt, "HH:mm:ss")
      : null;

    return {
      id: order.id,
      placedOrigin: order.placedOrigin,
      consumerId: order.consumer.id,
      name: order.consumer.name,
      phone: order.consumer.phone,
      address: formatFullAddress(order.delivery.address),
      coverageArea: order.coverageArea.name,
      eta: deliveryTime
        ? `${deliveryTime} (${order.delivery.eta?.current} ${t("minutes")})`
        : t("undefined"),
    };
  });

  const handleOpenChat = (orderId: string) => {
    const orderData = opennedChats.find((order: any) => order.id === orderId);
    setCurrentChat(orderData);
  };

  const handleCloseChat = () => {
    setCurrentChat(null);
  };

  const sendMessage = () => {
    if (!inputMessage) return;

    setInputMessage("");

    handleSendNewMessage({
      orderId: currentChat.id,
      fromId: dealerId,
      toId: currentChat.consumerId,
      message: inputMessage,
    });
  };

  const modRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    modRef.current?.scrollTo({
      top: modRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [currentChat, chatData]);

  return (
    <Styled.Container>
      <Styled.ContainerButton>
        <Button
          onClick={() => navigate("/orders/manage")}
          color="inherit"
          variant="text"
        >
          <ArrowBackIcon />
          {t("back")}
        </Button>
      </Styled.ContainerButton>
      <Styled.Grid>
        <Styled.GridLeft>
          <Styled.CardContainer space="8px 16px">
            <Typography variant="h6" textAlign="start">
              {t("deliveriesInProgress")}
            </Typography>
          </Styled.CardContainer>

          {opennedChats.map((data: any) => (
            <Styled.CardContainerButton
              key={data.name}
              space="24px 36px 32px 24px"
              onClick={() => handleOpenChat(data.id)}
            >
              <Styled.ContainerTitle>
                <Typography variant="body1" textAlign="start" fontWeight="bold">
                  {data.name}
                </Typography>
                <Styled.Time>
                  <QueryBuilderIcon color="disabled" fontSize="small" />
                  <Typography variant="body2" textAlign="start">
                    {t("deliverBy")} {data.eta}
                  </Typography>
                </Styled.Time>
              </Styled.ContainerTitle>

              {hasNewMessage(getChatMessages(data.id)) && (
                <Styled.NewMessageBadge />
              )}

              <Styled.ContainerArrow>
                <ArrowForwardIosIcon color="disabled" />
              </Styled.ContainerArrow>
            </Styled.CardContainerButton>
          ))}
        </Styled.GridLeft>

        {currentChat && (
          <Styled.GridRight>
            <Styled.CardContainer space="8px 16px">
              <Typography variant="h6" textAlign="start">
                {t("message")}
              </Typography>
            </Styled.CardContainer>

            <Styled.CardContainer space="24px 24px">
              <Styled.ContainerChat>
                <Styled.ContainerHeader>
                  <Styled.ContainerDatasers>
                    <Styled.ContainerPlaceOrigin>
                      <Typography
                        variant="h6"
                        textAlign="start"
                        marginBottom={1}
                      >
                        {currentChat.name}{" "}
                      </Typography>

                      <Styled.ContentPlaceOrigin>
                        {currentChat?.placedOrigin === "ConsumerApp"
                          ? "VIA APLICATIVO"
                          : currentChat?.placedOrigin === "DealerWhatsApp" ||
                            currentChat?.placedOrigin === "ConsumerWhatsApp"
                          ? "VIA WHATSAPP"
                          : currentChat?.placedOrigin === "DealerStore" ||
                            currentChat?.placedOrigin === "DriverStore"
                          ? "VIA PORTARIA"
                          : "Não informado"}
                      </Styled.ContentPlaceOrigin>
                    </Styled.ContainerPlaceOrigin>

                    <Box display="flex" pb="5">
                      <QueryBuilderIcon
                        fontSize="small"
                        sx={{ marginRight: 1, marginBottom: 1 }}
                      />
                      <Typography variant="body2" textAlign="start">
                        {t("deliverBy")} {currentChat.eta}
                      </Typography>
                    </Box>

                    <Box display="flex" pb="1">
                      <PhoneIcon
                        fontSize="small"
                        sx={{ marginRight: 1, marginBottom: 1 }}
                      />
                      <Typography variant="body2" textAlign="start">
                        {currentChat?.phone && currentChat?.phone?.length >= 10
                          ? formatPhoneByText(
                              currentChat?.phone
                                ?.replace(/[^0-9]+/g, "")
                                ?.replace(/^[ ]*55([\s\S]+)/, "$1") || "",
                            )
                          : currentChat?.phone
                              ?.replace(/[^0-9]+/g, "")
                              ?.replace(/^[ ]*55([\s\S]+)/, "$1" || "")}
                      </Typography>
                    </Box>

                    <Box display="flex" pb="1">
                      <LocationOnIcon
                        fontSize="small"
                        sx={{ marginRight: 1 }}
                      />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="GrayText"
                          textAlign="start"
                        >
                          Endereço para entrega
                        </Typography>
                        <Typography variant="h6" textAlign="start" ml="5">
                          {currentChat.address}
                        </Typography>
                        <Typography variant="body2" textAlign="start">
                          {currentChat.coverageArea}
                        </Typography>
                      </Box>
                    </Box>
                  </Styled.ContainerDatasers>
                  <Box>
                    <Button
                      onClick={handleCloseChat}
                      color="inherit"
                      variant="text"
                      endIcon={<CloseIcon />}
                      type="button"
                    />
                  </Box>
                </Styled.ContainerHeader>
                <Styled.ContainerMessages ref={modRef}>
                  {currentChat?.placedOrigin !== "ConsumerApp" && (
                    <Styled.ContainerMessage>
                      <Typography textAlign="center" ml="5">
                        O canal que este pedido foi feito não possibilita envio
                        de mensagem. Entre em contato pelo telefone informado.
                      </Typography>
                    </Styled.ContainerMessage>
                  )}

                  {currentChat?.placedOrigin === "ConsumerApp" &&
                    getChatMessages(currentChat.id)?.map(
                      (message: MessageData) => (
                        <>
                          <Styled.Message
                            key={message.message}
                            variant="body2"
                            textAlign="center"
                            alignSelf={
                              message.fromId === dealerId
                                ? "flex-end"
                                : "flex-start"
                            }
                          >
                            <Typography
                              sx={{
                                wordBreak: "break-word",
                                wordWrap: "break-word",
                              }}
                            >
                              {message.message}
                            </Typography>
                          </Styled.Message>
                          <Typography
                            color="rgba(0, 0, 0, 0.38)"
                            fontSize={12}
                            alignSelf={
                              message.fromId === dealerId
                                ? "flex-end"
                                : "flex-start"
                            }
                          >
                            {format(new Date(message.time || Date()), "HH:mm")}
                          </Typography>
                        </>
                      ),
                    )}
                </Styled.ContainerMessages>

                {currentChat?.placedOrigin === "ConsumerApp" && (
                  <Styled.MessagesInput>
                    <FormControl sx={{ m: 1 }} variant="filled">
                      <FilledInput
                        id="filled-adornment-password"
                        type="text"
                        value={inputMessage}
                        placeholder={t("message")}
                        onKeyPress={(ev) => {
                          if (ev.key === "Enter") {
                            sendMessage();
                          }
                        }}
                        onChange={(ev) => setInputMessage(ev.target.value)}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={sendMessage}
                              edge="end"
                              sx={{ marginRight: 0.5 }}
                            >
                              <SendIcon />
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                    </FormControl>
                  </Styled.MessagesInput>
                )}
              </Styled.ContainerChat>
            </Styled.CardContainer>
          </Styled.GridRight>
        )}
      </Styled.Grid>
    </Styled.Container>
  );
};

export default Chat;
